import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useRef, useState } from "react";
import { uploadCmsImage } from "@/lib/api";
import { toast } from "sonner";
import {
    Bold,
    Italic,
    List,
    ListOrdered,
    Heading2,
    Heading3,
    Quote,
    Link as LinkIcon,
    Link2,
    Image as ImageIcon,
    Undo,
    Redo,
} from "lucide-react";

// Curated set of internal targets for the "Insert internal link" helper.
// Surface what's truly useful — admins can still paste any URL via the
// generic Link button.
const INTERNAL_TARGETS = [
    { label: "Home", path: "/" },
    { label: "How It Works", path: "/how-it-works" },
    { label: "Pricing", path: "/pricing" },
    { label: "For Buyers", path: "/for-buyers" },
    { label: "For Investors", path: "/for-investors" },
    { label: "Sample Analysis", path: "/sample-analysis" },
    { label: "Upload Inspection", path: "/upload" },
    { label: "Learn — Hub", path: "/learn" },
    { label: "Contact", path: "/contact" },
    { label: "Support / Help Center", path: "/support" },
    { label: "Privacy Policy", path: "/legal/privacy" },
    { label: "Terms of Service", path: "/legal/terms" },
];

function ToolbarButton({ active, onClick, children, label }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className={
                "px-2.5 py-1.5 rounded-md text-sm transition-colors " +
                (active
                    ? "bg-bl-primary text-bl-surface"
                    : "text-bl-muted hover:bg-bl-bg")
            }
            data-testid={`tiptap-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            {children}
        </button>
    );
}

/**
 * Rich-text editor used by the CMS. Wraps Tiptap with our own toolbar so the
 * UI matches the Buyer's Leverage design system.
 *
 * - `value` is the current HTML string.
 * - `onChange(html)` fires on every editor update — caller debounces to autosave.
 */
export default function RichTextEditor({ value, onChange, placeholder }) {
    const fileInputRef = useRef(null);
    const [internalOpen, setInternalOpen] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
                link: false, // we use the standalone Link extension below
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: "text-bl-primary underline underline-offset-2",
                },
            }),
            Image.configure({
                inline: false,
                allowBase64: false,
                HTMLAttributes: {
                    class: "rounded-md max-w-full h-auto",
                },
            }),
            Placeholder.configure({
                placeholder:
                    placeholder ||
                    "Start writing. Use ## for sections, ### for sub-sections…",
            }),
        ],
        content: value || "",
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: "tiptap-content prose prose-sm max-w-none min-h-[320px] focus:outline-none",
                "data-testid": "cms-tiptap-editor",
            },
        },
    });

    // Keep editor in sync when `value` changes externally (e.g. on load).
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if (value && current !== value) {
            editor.commands.setContent(value, false);
        }
    }, [value, editor]);

    if (!editor) return null;

    const insertLink = () => {
        const url = window.prompt("URL");
        if (!url) return;
        editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
    };

    const insertInternalLink = (path) => {
        // If there's no selection, insert the label as the link text.
        const { from, to } = editor.state.selection;
        const hasSelection = from !== to;
        const target = INTERNAL_TARGETS.find((t) => t.path === path);
        if (!target) return;
        if (hasSelection) {
            editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: target.path })
                .run();
        } else {
            editor
                .chain()
                .focus()
                .insertContent(
                    `<a href="${target.path}" class="text-bl-primary underline underline-offset-2">${target.label}</a>`,
                )
                .run();
        }
        setInternalOpen(false);
    };

    const onImagePick = () => {
        fileInputRef.current?.click();
    };

    const onImageFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image must be ≤ 8MB");
            return;
        }
        setUploadingImage(true);
        try {
            const r = await uploadCmsImage(file);
            // Use the absolute backend URL so the image renders on the public site.
            const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
            const url = `${base}${r.url}`;
            editor.chain().focus().setImage({ src: url, alt: file.name }).run();
            toast.success("Image inserted.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Image upload failed.");
        } finally {
            setUploadingImage(false);
        }
    };

    return (
        <div
            className="bl-card overflow-hidden"
            data-testid="cms-rich-text-editor"
        >
            <div className="flex items-center gap-1 flex-wrap px-3 py-2 border-b border-bl bg-bl-bg/40">
                <ToolbarButton
                    label="Heading 2"
                    active={editor.isActive("heading", { level: 2 })}
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 2 }).run()
                    }
                >
                    <Heading2 className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                    label="Heading 3"
                    active={editor.isActive("heading", { level: 3 })}
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 3 }).run()
                    }
                >
                    <Heading3 className="w-4 h-4" />
                </ToolbarButton>
                <span className="w-px h-5 bg-bl-border mx-1" />
                <ToolbarButton
                    label="Bold"
                    active={editor.isActive("bold")}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <Bold className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                    label="Italic"
                    active={editor.isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <Italic className="w-4 h-4" />
                </ToolbarButton>
                <span className="w-px h-5 bg-bl-border mx-1" />
                <ToolbarButton
                    label="Bullets"
                    active={editor.isActive("bulletList")}
                    onClick={() =>
                        editor.chain().focus().toggleBulletList().run()
                    }
                >
                    <List className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                    label="Numbered"
                    active={editor.isActive("orderedList")}
                    onClick={() =>
                        editor.chain().focus().toggleOrderedList().run()
                    }
                >
                    <ListOrdered className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                    label="Quote"
                    active={editor.isActive("blockquote")}
                    onClick={() =>
                        editor.chain().focus().toggleBlockquote().run()
                    }
                >
                    <Quote className="w-4 h-4" />
                </ToolbarButton>
                <span className="w-px h-5 bg-bl-border mx-1" />
                <ToolbarButton
                    label="Link"
                    active={editor.isActive("link")}
                    onClick={insertLink}
                >
                    <LinkIcon className="w-4 h-4" />
                </ToolbarButton>
                <div className="relative">
                    <ToolbarButton
                        label="Internal Link"
                        onClick={() => setInternalOpen((v) => !v)}
                    >
                        <Link2 className="w-4 h-4" />
                    </ToolbarButton>
                    {internalOpen && (
                        <div
                            className="absolute z-20 mt-1 left-0 w-56 bl-card p-1 shadow-lg"
                            data-testid="tiptap-internal-link-menu"
                            onMouseLeave={() => setInternalOpen(false)}
                        >
                            {INTERNAL_TARGETS.map((t) => (
                                <button
                                    key={t.path}
                                    type="button"
                                    onClick={() => insertInternalLink(t.path)}
                                    className="w-full text-left px-2.5 py-1.5 rounded text-xs text-bl hover:bg-bl-teal-tint/40"
                                    data-testid={`tiptap-internal-link-${t.path.replace(/\//g, "_") || "home"}`}
                                >
                                    <div className="font-medium">{t.label}</div>
                                    <div className="text-bl-muted text-[10px] font-mono-bl">
                                        {t.path}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <ToolbarButton
                    label="Image"
                    onClick={onImagePick}
                >
                    <ImageIcon className={"w-4 h-4 " + (uploadingImage ? "opacity-40" : "")} />
                </ToolbarButton>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={onImageFile}
                    className="hidden"
                    data-testid="tiptap-image-file-input"
                />
                <span className="ml-auto flex gap-1">
                    <ToolbarButton
                        label="Undo"
                        onClick={() => editor.chain().focus().undo().run()}
                    >
                        <Undo className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        label="Redo"
                        onClick={() => editor.chain().focus().redo().run()}
                    >
                        <Redo className="w-4 h-4" />
                    </ToolbarButton>
                </span>
            </div>
            <div className="p-5 bg-bl-bg">
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}
